import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../services/toast.service';

/**
 * Fixed-position stack of toast notifications (top-right). Renders the live list
 * from ToastService; click a toast (or its ×) to dismiss it early. Mounted once
 * in AppComponent so it's available on every route.
 */
@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toast-host" role="status" aria-live="polite">
      <div
        *ngFor="let t of toasts.toasts"
        class="toast-item"
        [ngClass]="'toast-item--' + t.type"
        (click)="toasts.dismiss(t.id)"
      >
        <span class="toast-item__msg">{{ t.message }}</span>
        <button
          class="toast-item__close"
          type="button"
          aria-label="Dismiss"
          (click)="$event.stopPropagation(); toasts.dismiss(t.id)"
        >×</button>
      </div>
    </div>
  `,
  styles: [`
    .toast-host {
      position: fixed;
      top: 1rem;
      right: 1rem;
      z-index: 20000; /* above modals/overlays */
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-width: min(90vw, 380px);
      pointer-events: none; /* let clicks through the empty gaps */
    }
    .toast-item {
      pointer-events: auto;
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.75rem 0.9rem;
      border-radius: 10px;
      background: var(--quine-surface-elevated, var(--quine-surface, #1c1c22));
      color: var(--quine-text-primary, var(--quine-text, #f2f2f5));
      border: 1px solid var(--quine-border, rgba(255, 255, 255, 0.1));
      border-left: 3px solid var(--toast-accent, rgb(var(--quine-accent-rgb, 129 140 248)));
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
      font-size: 0.875rem;
      line-height: 1.35;
      cursor: pointer;
      animation: toast-in 160ms ease-out;
    }
    .toast-item--info    { --toast-accent: rgb(var(--quine-accent-rgb, 129 140 248)); }
    .toast-item--success { --toast-accent: #4ade80; }
    .toast-item--warning { --toast-accent: #fbbf24; }
    .toast-item--error   { --toast-accent: #f87171; }
    .toast-item__msg { flex: 1; }
    .toast-item__close {
      flex: none;
      background: none;
      border: none;
      color: var(--quine-muted, rgba(255, 255, 255, 0.55));
      font-size: 1.1rem;
      line-height: 1;
      cursor: pointer;
      padding: 0 0.15rem;
    }
    .toast-item__close:hover { color: var(--quine-text-primary, #fff); }
    @keyframes toast-in {
      from { opacity: 0; transform: translateX(12px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `]
})
export class ToastComponent {
  constructor(public toasts: ToastService) {}
}
